import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Disc3, CheckSquare, Square, Merge, X } from 'lucide-react'
import { albums } from '../api/client'
import type { AlbumWithArtist } from '../types'

export default function Albums() {
  const queryClient = useQueryClient()
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [targetAlbum, setTargetAlbum] = useState<AlbumWithArtist | null>(null)

  // Use browse endpoint to show ALL albums (not filtered by collection)
  const { data: albumList, isLoading } = useQuery({
    queryKey: ['albums', 'browse'],
    queryFn: () => albums.browse(1000, 0),
  })

  const mergeMutation = useMutation({
    mutationFn: async ({ sourceIds, targetId }: { sourceIds: string[]; targetId: string }) => {
      let totalTracks = 0
      for (const sourceId of sourceIds) {
        if (sourceId !== targetId) {
          const result = await albums.merge(sourceId, targetId)
          totalTracks += result.tracks_moved
        }
      }
      return { tracks_moved: totalTracks }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['albums', 'browse'] })
      queryClient.invalidateQueries({ queryKey: ['artists', 'browse'] })
      setSelectMode(false)
      setSelectedIds(new Set())
      setShowMergeModal(false)
      setTargetAlbum(null)
      alert(`Merged successfully! Moved ${result.tracks_moved} tracks.`)
    },
    onError: (error) => {
      console.error('Merge failed:', error)
      alert('Failed to merge albums. Please try again.')
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const selectedAlbums = albumList?.filter((a) => selectedIds.has(a.id)) || []

  const handleMergeClick = () => {
    if (selectedAlbums.length < 2) return
    setShowMergeModal(true)
  }

  const handleConfirmMerge = () => {
    if (!targetAlbum) return
    const sourceIds = Array.from(selectedIds)
    mergeMutation.mutate({ sourceIds, targetId: targetAlbum.id })
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Albums</h1>
        <div className="flex items-center gap-2">
          {selectMode && selectedIds.size >= 2 && (
            <button
              onClick={handleMergeClick}
              className="btn btn-primary flex items-center gap-2"
            >
              <Merge size={18} />
              Merge ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={`btn ${selectMode ? 'btn-secondary' : 'btn-secondary'} flex items-center gap-2`}
          >
            {selectMode ? (
              <>
                <X size={18} />
                Cancel
              </>
            ) : (
              <>
                <CheckSquare size={18} />
                Select
              </>
            )}
          </button>
        </div>
      </div>

      {selectMode && (
        <div className="mb-4 p-3 bg-dark-800 rounded-lg text-sm text-dark-300">
          Select 2 or more albums to merge them. All tracks will be combined into the album you choose to keep.
        </div>
      )}

      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : albumList && albumList.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {albumList.map((album) => {
            const isSelected = selectedIds.has(album.id)
            return selectMode ? (
              <div
                key={album.id}
                onClick={() => toggleSelect(album.id)}
                className={`card group cursor-pointer transition-colors ${
                  isSelected ? 'bg-primary-600/20 ring-2 ring-primary-500' : 'hover:bg-dark-700'
                }`}
              >
                <div className="absolute top-2 right-2 z-10">
                  {isSelected ? (
                    <CheckSquare size={24} className="text-primary-500" />
                  ) : (
                    <Square size={24} className="text-dark-500" />
                  )}
                </div>
                <div className="aspect-square mb-4 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden">
                  {album.cover_path ? (
                    <img
                      src={`/api/image/${encodeURIComponent(album.cover_path)}`}
                      alt={album.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Disc3 size={48} className="text-dark-500" />
                  )}
                </div>
                <h3 className="font-medium truncate">{album.title}</h3>
                <p className="text-sm text-dark-400 truncate">
                  {album.artist_name || 'Unknown Artist'}
                </p>
                {album.year && <p className="text-xs text-dark-500">{album.year}</p>}
              </div>
            ) : (
              <Link
                key={album.id}
                to={`/albums/${album.id}`}
                className="card group hover:bg-dark-700 transition-colors"
              >
                <div className="aspect-square mb-4 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden">
                  {album.cover_path ? (
                    <img
                      src={`/api/image/${encodeURIComponent(album.cover_path)}`}
                      alt={album.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Disc3 size={48} className="text-dark-500" />
                  )}
                </div>
                <h3 className="font-medium truncate group-hover:text-primary-400">
                  {album.title}
                </h3>
                <p className="text-sm text-dark-400 truncate">
                  {album.artist_name || 'Unknown Artist'}
                </p>
                {album.year && <p className="text-xs text-dark-500">{album.year}</p>}
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Disc3 size={48} className="mx-auto text-dark-500 mb-4" />
          <p className="text-dark-400 text-lg mb-2">No albums yet</p>
          <p className="text-dark-500 text-sm">
            Scan a music library to see albums here
          </p>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Merge Albums</h2>
            <p className="text-dark-300 mb-4">
              Select which album to keep. All tracks from the other albums will be moved to this
              album.
            </p>
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {selectedAlbums.map((album) => (
                <button
                  key={album.id}
                  onClick={() => setTargetAlbum(album)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    targetAlbum?.id === album.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-700 hover:bg-dark-600'
                  }`}
                >
                  <div className="w-10 h-10 rounded bg-dark-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {album.cover_path ? (
                      <img
                        src={`/api/image/${encodeURIComponent(album.cover_path)}`}
                        alt={album.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Disc3 size={20} className="text-dark-400" />
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <span className="font-medium truncate block">{album.title}</span>
                    <span className="text-sm text-dark-400 truncate block">
                      {album.artist_name}{album.year && ` (${album.year})`}
                    </span>
                  </div>
                  {targetAlbum?.id === album.id && (
                    <span className="text-sm bg-primary-700 px-2 py-0.5 rounded flex-shrink-0">
                      Keep
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMergeModal(false)
                  setTargetAlbum(null)
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMerge}
                disabled={!targetAlbum || mergeMutation.isPending}
                className="btn btn-primary flex-1 disabled:opacity-50"
              >
                {mergeMutation.isPending ? 'Merging...' : 'Merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
